import { BIDDER_RULE_SECTION_LABELS, BIDDER_RULE_SECTIONS, BidderRule, sectionOfBidderSeat } from '@pubstack/common/src/bidder-rule'
import { FunctionComponent, useMemo } from 'react'
import { useBidderSeats } from '~/api/bidder-seats.api'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { Spinner } from '~/components/Spinner'
import { BidderMediaTypeList } from '~/feature-components/BidderMediaTypeList'
import { bySeatName } from '~/modules/monetization/inventory/bidder-rules/bidderRuleSections'

type Props = {
  bidderRule: BidderRule
  /** Leaves the form for the rule editor. */
  onEdit?: () => void
  isEditable?: boolean
}

/**
 * The stack's single bidder rule, split into the three sections the document stores.
 *
 * Its own component rather than `ViewBidderRuleFlyout`, which merges the legacy section into server-side.
 */
export const StackBidderRuleFlyout: FunctionComponent<Props> = ({ bidderRule, onEdit, isEditable }) => {
  const { data: admSeats, isLoading: isLoadingAdm } = useBidderSeats('adm')
  const { data: pbsSeats, isLoading: isLoadingPbs } = useBidderSeats('pbs')

  const seatsById = useMemo(() => Object.fromEntries([...admSeats, ...pbsSeats].map((seat) => [seat.id, seat])), [admSeats, pbsSeats])

  // Grouped by the stored section, so `Server-side One (legacy)` stands on its own line instead of being
  // merged into server-side, as the rules pages do.
  //
  // Sorted by name, and a selected seat whose id no longer resolves is skipped rather than rendered
  // blank: a rule can outlive a deleted seat.
  const sections = useMemo(
    () =>
      BIDDER_RULE_SECTIONS.map((section) => ({
        section,
        entries: bidderRule.selectedBidderSeats
          .flatMap((entry) => {
            const seat = seatsById[entry.id]
            return seat && sectionOfBidderSeat(seat) === section ? [{ seat, mediaTypes: entry.mediaTypes }] : []
          })
          .sort(bySeatName),
      })),
    [bidderRule.selectedBidderSeats, seatsById]
  )

  return (
    <Flyout.Content>
      <Flyout.Title>{bidderRule.name} - details</Flyout.Title>
      <Flyout.Body>
        {isLoadingAdm || isLoadingPbs ? (
          <Spinner />
        ) : (
          <div className={'flex flex-col gap-6'}>
            {sections.map(({ section, entries }) => (
              <section key={section} className={'flex flex-col gap-2'}>
                <h3 className={'font-bold'}>{BIDDER_RULE_SECTION_LABELS[section]}</h3>
                <BidderMediaTypeList rows={entries.map(({ seat, mediaTypes }) => ({ key: seat.id, label: seat.name, mediaTypes }))} emptyLabel={'No bidder enabled.'} />
              </section>
            ))}
          </div>
        )}
      </Flyout.Body>
      {/* In the footer, where every view flyout keeps its way out of reading and into editing. */}
      {onEdit && isEditable && (
        <Flyout.Actions>
          <Button variant="secondary" iconName="edit" onClick={onEdit} trackingLabel="common-bidder-rules-edit">
            Edit rule
          </Button>
        </Flyout.Actions>
      )}
    </Flyout.Content>
  )
}
